/**
 * Human-readable label map for tables and reports.
 * Covers OASIS visit types, and clinical visit types. Uses long-form labels
 * (e.g. "OASIS Start of Care").
 */
export const VISIT_TYPE_LABELS: Record<string, string> = {
  OASIS_E2_START_OF_CARE: 'OASIS Start of Care (Legacy)',
  OASIS_E2_START_OF_CARE_ENHANCED: 'OASIS Start of Care',
  OASIS_E2_RESUMPTION_OF_CARE: 'OASIS Resumption of Care (Legacy)',
  OASIS_E2_RESUMPTION_OF_CARE_ENHANCED: 'OASIS Resumption of Care',
  OASIS_E2_RECERTIFICATION: 'OASIS Recertification (Legacy)',
  OASIS_E2_RECERTIFICATION_ENHANCED: 'OASIS Recertification',
  OASIS_E2_DISCHARGE: 'OASIS Discharge (Legacy)',
  OASIS_E2_DISCHARGE_ENHANCED: 'OASIS Discharge',
  OASIS_E2_DISCHARGE_NON_BILLABLE: 'OASIS Discharge (Non-billable)',
  OASIS_E2_START_OF_CARE_PT: 'OASIS Start of Care (PT, Legacy)',
  OASIS_E2_START_OF_CARE_PT_ENHANCED: 'OASIS Start of Care (PT)',
  OASIS_E2_RESUMPTION_OF_CARE_PT: 'OASIS Resumption of Care (PT, Legacy)',
  OASIS_E2_RESUMPTION_OF_CARE_PT_ENHANCED: 'OASIS Resumption of Care (PT)',
  OASIS_E2_RECERTIFICATION_PT: 'OASIS Recertification (PT, Legacy)',
  OASIS_E2_RECERTIFICATION_PT_ENHANCED: 'OASIS Recertification (PT)',
  OASIS_E2_DISCHARGE_PT: 'OASIS Discharge (PT, Legacy)',
  OASIS_E2_DISCHARGE_PT_ENHANCED: 'OASIS Discharge (PT)',
  OASIS_E2_DISCHARGE_PT_NON_BILLABLE: 'OASIS Discharge (PT) (Non-billable)',
  OASIS_E2_START_OF_CARE_OT: 'OASIS Start of Care (OT, Legacy)',
  OASIS_E2_START_OF_CARE_OT_ENHANCED: 'OASIS Start of Care (OT)',
  OASIS_E2_RESUMPTION_OF_CARE_OT: 'OASIS Resumption of Care (OT, Legacy)',
  OASIS_E2_RESUMPTION_OF_CARE_OT_ENHANCED: 'OASIS Resumption of Care (OT)',
  OASIS_E2_RECERTIFICATION_OT: 'OASIS Recertification (OT, Legacy)',
  OASIS_E2_RECERTIFICATION_OT_ENHANCED: 'OASIS Recertification (OT)',
  OASIS_E2_DISCHARGE_OT: 'OASIS Discharge (OT, Legacy)',
  OASIS_E2_DISCHARGE_OT_ENHANCED: 'OASIS Discharge (OT)',
  OASIS_E2_DISCHARGE_OT_NON_BILLABLE: 'OASIS Discharge (OT) (Non-billable)',
  OASIS_E2_START_OF_CARE_ST: 'OASIS Start of Care (ST, Legacy)',
  OASIS_E2_START_OF_CARE_ST_ENHANCED: 'OASIS Start of Care (ST)',
  OASIS_E2_RESUMPTION_OF_CARE_ST: 'OASIS Resumption of Care (ST, Legacy)',
  OASIS_E2_RESUMPTION_OF_CARE_ST_ENHANCED: 'OASIS Resumption of Care (ST)',
  OASIS_E2_RECERTIFICATION_ST: 'OASIS Recertification (ST, Legacy)',
  OASIS_E2_RECERTIFICATION_ST_ENHANCED: 'OASIS Recertification (ST)',
  OASIS_E2_DISCHARGE_ST: 'OASIS Discharge (ST, Legacy)',
  OASIS_E2_DISCHARGE_ST_ENHANCED: 'OASIS Discharge (ST)',
  OASIS_E2_DISCHARGE_ST_NON_BILLABLE: 'OASIS Discharge (ST) (Non-billable)',
  OASIS_E2_FOLLOW_UP: 'OASIS Follow-up (Legacy)',
  OASIS_E2_FOLLOW_UP_ENHANCED: 'OASIS Follow-up',
  OASIS_E2_FOLLOW_UP_PT: 'OASIS Follow-up (PT, Legacy)',
  OASIS_E2_FOLLOW_UP_PT_ENHANCED: 'OASIS Follow-up (PT)',
  OASIS_E2_FOLLOW_UP_OT: 'OASIS Follow-up (OT, Legacy)',
  OASIS_E2_FOLLOW_UP_OT_ENHANCED: 'OASIS Follow-up (OT)',
  OASIS_E2_FOLLOW_UP_ST: 'OASIS Follow-up (ST, Legacy)',
  OASIS_E2_FOLLOW_UP_ST_ENHANCED: 'OASIS Follow-up (ST)',
  OASIS_E2_TRANSFER: 'OASIS Transfer (Legacy)',
  OASIS_E2_TRANSFER_ENHANCED: 'OASIS Transfer',
  OASIS_E2_TRANSFER_PT: 'OASIS Transfer (PT, Legacy)',
  OASIS_E2_TRANSFER_PT_ENHANCED: 'OASIS Transfer (PT)',
  OASIS_E2_TRANSFER_OT: 'OASIS Transfer (OT, Legacy)',
  OASIS_E2_TRANSFER_OT_ENHANCED: 'OASIS Transfer (OT)',
  OASIS_E2_TRANSFER_ST: 'OASIS Transfer (ST, Legacy)',
  OASIS_E2_TRANSFER_ST_ENHANCED: 'OASIS Transfer (ST)',
  OASIS_E2_DEATH: 'OASIS Death (Legacy)',
  OASIS_E2_DEATH_ENHANCED: 'OASIS Death',
  OASIS_E2_DEATH_PT: 'OASIS Death (PT, Legacy)',
  OASIS_E2_DEATH_PT_ENHANCED: 'OASIS Death (PT)',
  OASIS_E2_DEATH_OT: 'OASIS Death (OT, Legacy)',
  OASIS_E2_DEATH_OT_ENHANCED: 'OASIS Death (OT)',
  OASIS_E2_DEATH_ST: 'OASIS Death (ST, Legacy)',
  OASIS_E2_DEATH_ST_ENHANCED: 'OASIS Death (ST)',
  RN: 'SN Visit',
  RN_SKILLED_NURSING_VISIT: 'RN Skilled Nursing Visit',
  LPN_LVN_SKILLED_NURSING_VISIT: 'LPN/LVN Skilled Nursing Visit',
  // Additional SNV aliases (all RN note form)
  SKILLED_NURSING_VISIT: 'Skilled Nursing Visit',
  PRN_SKILLED_NURSING_VISIT: 'PRN Skilled Nursing Visit',
  WOUND_CARE_VISIT: 'Wound Care Visit',
  INFUSION_THERAPY_VISIT: 'Infusion / IV Therapy Visit',
  CATHETER_CARE_VISIT: 'Catheter Care Visit',
  OSTOMY_CARE_VISIT: 'Ostomy Care Visit',
  DIABETIC_MANAGEMENT_VISIT: 'Diabetic Management Visit',
  MEDICATION_MANAGEMENT_VISIT: 'Medication Management Visit',
  INJECTION_VISIT: 'Injection Visit',
  LPN_LVN_WOUND_CARE_VISIT: 'LPN/LVN - Wound Care Visit',
  PICC_LABS: 'PICC Labs',
  PICC_VISIT: 'PICC Visit',
  SN_LABS: 'SN Labs',
  FOLEY_CATHETER_CHANGE: 'Foley Catheter Change',
  WOUND_VAC_VISIT: 'Wound Vac Visit',
  PT_W_INR: 'PT W/ INR',
  SN_VISIT_WITH_SUPERVISORY_VISIT: 'SN Visit w/ Supervisory Visit',
  SN_VISIT_WITH_DISCHARGE_SUMMARY: 'SN Visit w/ Discharge Summary',
  LVN_SUPERVISORY_VISIT: 'LVN Supervisory Visit',
  PTA_SUPERVISORY_VISIT: 'PTA Supervisory Visit',
  COTA_SUPERVISORY_VISIT: 'COTA Supervisory Visit',
  HHA_SUPERVISORY_VISIT: 'HHA Supervisory Visit',
  AIDE_SUPERVISORY_VISIT: 'Aide Supervisory Visit',
  CASE_CONFERENCE_60_DAY_SUMMARY: 'Case Conference & 60 Day Summary',
  SN_DISCHARGE_SUMMARY: 'SN Discharge Summary',
  PT_DISCHARGE_SUMMARY: 'PT Discharge Summary',
  OT_DISCHARGE_SUMMARY: 'OT Discharge Summary',
  ST_DISCHARGE_SUMMARY: 'ST Discharge Summary',
  SN_TRANSFER_SUMMARY: 'SN Transfer Summary',
  PT_VISIT: 'PT Visit',
  PTA_VISIT: 'PTA Visit',
  PT_RE_EVALUATION: 'PT Re-Evaluation',
  PT_TREATMENT_VISIT: 'PT Treatment Visit',
  PT_DISCHARGE: 'PT Discharge',
  OT_VISIT: 'OT Visit',
  COTA_VISIT: 'COTA Visit',
  OT_RE_EVALUATION: 'OT Re-Evaluation',
  OT_TREATMENT_VISIT: 'OT Treatment Visit',
  ST_VISIT: 'ST Visit',
  MSW_EVALUATION: 'MSW Evaluation',
  MSW_VISIT: 'MSW Visit',
  MSW_DISCHARGE: 'MSW Discharge',
  AIDE_CARE_PLAN: 'Aide Care Plan',
  AIDE_CARE_PLAN_REVISION: 'Aide Care Plan Revision',
  HHA_VISIT: 'HHA Visit',
  PT_EVALUATION: 'PT Evaluation',
  PT_RE_EVALUATION_WITH_SUPERVISORY_VISIT:
    'PT Re-Evaluation w/ Supervisory Visit',
  OT_EVALUATION: 'OT Evaluation',
  ST_EVALUATION: 'ST Evaluation',
};
